import { useEffect, useState } from 'react';
import { api, setToken, type Account, type Brand, type Defaults, type Workspace } from '../lib/api';
import { DefaultsPanel } from './Defaults';
import { WorkspaceModels } from './WorkspaceModels';
import { CloseWorkspace } from './CloseWorkspace';
import { Billing } from './Billing';
import { Members } from './Members';
import { DoorChecklist } from './DoorChecklist';
import { DoorEpisodes } from './DoorEpisodes';
import { ViewerQuestions } from './ViewerQuestions';

/**
 * Settings.
 *
 * There was none. Thomas walked every screen and said so: no way for a creator
 * to adjust anything about the platform. Three things that already worked had
 * no way in at all — the render budget, the invite endpoint, and changing a
 * password — and closing a workspace existed but was not reachable from the nav.
 *
 * Deliberately only what has a working endpoint behind it. An empty settings
 * screen full of controls that do nothing is worse than no settings screen,
 * because it looks finished.
 */
export function Settings({
  account,
  workspace,
  onBrandsChanged,
  onOpenCard,
  onOpenModels,
  defaults,
  onDefaults,
  brands,
}: {
  account: Account | null;
  workspace: Workspace | null;
  onBrandsChanged?: () => void;
  /** T211: a row of On the door opens its card on the deck. */
  onOpenCard?: (cardId: string) => void;
  /** 205: the catalogue, from the Models card. */
  onOpenModels?: () => void;
  /** 155: the workspace's defaults, and where to put them when they change. */
  defaults?: Defaults;
  onDefaults?: (d: Defaults) => void;
  brands?: Brand[];
}) {
  const [budget, setBudget] = useState<{ usedMinutes: number; limitMinutes: number | null } | null>(null);
  const [caps, setCaps] = useState<{ max_concurrent_renders: number; running_now: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    api.renders()
      .then((r) => { setBudget(r.budget); setCaps(r.limits); })
      .catch((e) => setError((e as Error).message));
  }, []);

  // ── password ──────────────────────────────────────────────────────────
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwBusy(true); setError(null); setNote(null);
    try {
      // Every other session dies here, including this tab's. The API hands back
      // a replacement so the person doing the changing is not signed out by
      // their own action — store it or the next request 401s.
      const r = await api.changePassword(cur, next);
      setToken(r.token);
      setCur(''); setNext('');
      setNote('Password changed. Every other signed-in device has been signed out.');
    } catch (e) { setError((e as Error).message); }
    setPwBusy(false);
  }

  // ── brands ────────────────────────────────────────────────────────────
  // A brand is a channel. Blue Heron runs five; the product allowed one.
  const [bName, setBName] = useState('');
  const [bBusy, setBBusy] = useState(false);

  // Derived, not typed. A slug is a URL-safe handle nobody should have to think
  // about, and letting somebody type one invites a mismatch between the name
  // they see and the address the storage keys use.
  const slugOf = (n: string) =>
    n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100);

  async function addBrand(e: React.FormEvent) {
    e.preventDefault();
    const slug = slugOf(bName);
    if (!slug) { setError('That name has no letters or numbers in it.'); return; }
    setBBusy(true); setError(null); setNote(null);
    try {
      const b = await api.createBrand(bName.trim(), slug);
      setNote(`Brand “${b.name}” created. Pick it in the selector at the top to work on it.`);
      setBName('');
      onBrandsChanged?.();
    } catch (e) { setError((e as Error).message); }
    finally { setBBusy(false); }
  }

  // ── invite ────────────────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [temp, setTemp] = useState('');
  const [invBusy, setInvBusy] = useState(false);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setInvBusy(true); setError(null); setNote(null);
    try {
      await api.invite({ email: email.trim(), password: temp, role });
      setNote(`${email.trim()} can sign in with the password you set. Tell them out of band — we cannot email it.`);
      setEmail(''); setTemp('');
    } catch (e) { setError((e as Error).message); }
    setInvBusy(false);
  }

  const pct = budget && budget.limitMinutes
    ? Math.min(100, Math.round((budget.usedMinutes / budget.limitMinutes) * 100))
    : 0;

  return (
    <>
      <div className="vhead">
        <div>
          <h2>Settings</h2>
          <p>This workspace, who is in it, and your own account.</p>
        </div>
      </div>

      {error && <div className="err">{error}</div>}
      {note && <div className="notice">{note}</div>}

      <section className="card">
        <h3>Workspace</h3>
        <dl className="kv">
          <dt>Name</dt><dd>{workspace?.name ?? '—'}</dd>
          <dt>Address</dt><dd><code>{workspace?.slug ?? '—'}</code></dd>
          <dt>Your role</dt><dd>{workspace?.role ?? '—'}</dd>
        </dl>
      </section>

      <section className="card">
        <h3>This month</h3>
        {budget === null ? (
          <p className="sub-dim">Loading…</p>
        ) : budget.limitMinutes === null ? (
          <p>Unmetered — this workspace has no monthly ceiling.</p>
        ) : (
          <>
            <p>
              <b>{budget.usedMinutes}</b> of <b>{budget.limitMinutes}</b> render minutes used.
              {' '}
              <span className="sub-dim">
                Counted when a render finishes, and reset on the first of the month.
              </span>
            </p>
            <div className="meter" aria-label={`${pct}% of the monthly budget used`}>
              <div className="meter-fill" style={{ width: `${pct}%` }} />
            </div>
            {caps && (
              <p className="sub-dim" style={{ marginTop: 10 }}>
                {caps.running_now} of {caps.max_concurrent_renders} renders running now.
                A render is refused when the month is spent, not queued and forgotten.
              </p>
            )}
          </>
        )}
      </section>

      <DoorChecklist />
      <DoorEpisodes onOpen={(id) => onOpenCard?.(id)} />
      <ViewerQuestions />
      <Billing role={workspace?.role ?? null} />
      <Members role={workspace?.role ?? null} />

      <section className="card">
        <h3>Brands</h3>
        <p className="sub-dim">
          A brand is a channel. Each one has its own colours, its own destinations and its
          own assets, and the selector at the top of the page switches between them.
        </p>
        <form onSubmit={addBrand} className="row">
          <input required maxLength={200} placeholder="Channel name — e.g. Blue Heron Media"
                 value={bName} onChange={(e) => setBName(e.target.value)} />
          <button className="btn pri" type="submit" disabled={bBusy || !bName.trim()}>
            {bBusy ? 'Creating…' : 'Add brand'}
          </button>
        </form>
        {bName.trim() && (
          <p className="sub-dim" style={{ marginTop: 6 }}>
            Its address will be <code>{slugOf(bName) || '—'}</code>.
          </p>
        )}
      </section>

      <section className="card">
        <h3>Invite someone</h3>
        <p className="sub-dim">
          Creates an account and adds it to this workspace. You set the first password and
          pass it on yourself — this deployment cannot send mail, so nothing is emailed.
          An address that already has a RookeryOS account cannot be invited.
        </p>
        <form onSubmit={invite} className="row">
          <input type="email" required placeholder="them@example.com"
                 value={email} onChange={(e) => setEmail(e.target.value)} />
          <input type="password" required minLength={12} placeholder="First password (12+)"
                 value={temp} onChange={(e) => setTemp(e.target.value)} />
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="viewer">viewer</option>
            <option value="member">member</option>
            <option value="editor">editor</option>
            <option value="admin">admin</option>
          </select>
          <button className="btn pri" type="submit" disabled={invBusy || !email || temp.length < 12}>
            {invBusy ? 'Inviting…' : 'Invite'}
          </button>
        </form>
      </section>

      <section className="card">
        <h3>Your account</h3>
        <dl className="kv">
          <dt>Signed in as</dt><dd>{account?.email ?? '—'}</dd>
        </dl>
        <CreatorDigestSwitch />
        <form onSubmit={changePassword} className="row" style={{ marginTop: 12 }}>
          <input type="password" required placeholder="Current password"
                 autoComplete="current-password"
                 value={cur} onChange={(e) => setCur(e.target.value)} />
          <input type="password" required minLength={12} placeholder="New password (12+)"
                 autoComplete="new-password"
                 value={next} onChange={(e) => setNext(e.target.value)} />
          <button className="btn" type="submit" disabled={pwBusy || !cur || next.length < 12}>
            {pwBusy ? 'Changing…' : 'Change password'}
          </button>
        </form>
      </section>

      {defaults && onDefaults && <WorkspaceModels value={defaults} onChange={onDefaults} onOpenCatalogue={onOpenModels} />}
      {defaults && onDefaults && <DefaultsPanel brands={brands ?? []} value={defaults} onChange={onDefaults} />}

      <CloseWorkspace workspace={workspace} />
    </>
  );
}

// The weekly email: plays, followers, comments, likes, what was published, the episode that did best.
function CreatorDigestSwitch() {
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => { api.creatorDigest().then(setOn).catch(() => setOn(null)); }, []);
  if (on === null) return null;
  return (
    <label className="row" style={{ marginTop: 10, gap: 8, fontSize: 13.5 }} data-tool="creator-digest">
      <input type="checkbox" checked={on} onChange={(e) => { const v = e.target.checked; setOn(v); api.setCreatorDigest(v).then(setOn).catch(() => setOn(!v)); }} />
      Email me my show's week on Rookery <span className="sub-dim">· plays, followers, comments, likes, the episode that did best · once a week, only when something happened</span>
    </label>
  );
}
